<template>
  <div
    ref="refSwiperContainer"
    class="reviews-mobile-swiper-container"
  >
    <!-- Skeleton пока Swiper не загружен -->
    <div 
      v-if="!swiperLoaded"
      class="reviews-mobile-swiper-skeleton"
    >
      <div 
        v-for="i in Math.min(reviews.length, 3)" 
        :key="`skeleton-${i}`"
        class="skeleton-slide"
      />
    </div>
    
    <!-- Swiper загружается только когда виден -->
    <div
      v-show="swiperLoaded"
      ref="refSwiper"
      class="reviews-mobile-swiper swiper"
      :counter="counter"
    >
      <div class="swiper-wrapper">
        <div
          v-for="(review) in reviews"
          :key="`review-${review.id}`"
          class="swiper-slide"
        >
          <ReviewCard
            :is-manager="isManager"
            :item="review"
          />
        </div>
      </div>
      <div
        ref="refSwiperPagination"
        class="swiper-pagination"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type Swiper from 'swiper';
import { safeDestroySwiper } from '~/utils/swiper/safeDestroySwiper';

const ReviewCard = defineAsyncComponent(() => import('~/components/reviews/ReviewCard.vue'));

interface IProps {
	reviews: IReviewItem[];
	isManager: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
  reviews: () => [],
  isManager: false
});

const counter: Ref<number> = ref(0);
const refSwiper: any = ref(null);
const refSwiperContainer: any = ref(null);
const refSwiperPagination: any = ref(null);
const swiperLoaded = ref(false);
let swiperInstance: Swiper | null = null;

// Динамическая загрузка Swiper только когда он нужен
const initSwiper = async () => {
  if (swiperInstance || !refSwiper.value) return;
  
  try {
    // Динамический импорт Swiper и его модулей
    // CSS Swiper загружается автоматически или добавлен глобально
    const [{ default: SwiperClass }, { Pagination }] = await Promise.all([
      import('swiper'),
      import('swiper/modules')
    ]);

    swiperInstance = new SwiperClass(refSwiper.value, {
      modules: [Pagination],
      pagination: {
        el: refSwiperPagination.value,
        enabled: true,
        type: 'bullets',
        clickable: true
      },
      spaceBetween: 16,
      slidesPerView: 1.1,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,
      breakpoints: {
        640: {
          slidesPerView: 2.2
        }
      }
    });
    
    swiperLoaded.value = true;
    counter.value = counter.value + 1;
  } catch (error) {
    console.error('Failed to initialize Swiper:', error);
  }
};

// Intersection Observer для lazy loading
let observer: IntersectionObserver | null = null;

onMounted(() => {
  if (!process.client || !refSwiperContainer.value) return;

  // Загружаем Swiper только когда блок становится видимым
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !swiperLoaded.value) {
          initSwiper();
          observer?.disconnect(); // Отключаем после первой загрузки
        }
      });
    },
    {
      rootMargin: '50px', // Предзагрузка за 50px до появления
      threshold: 0.1
    }
  );

  observer.observe(refSwiperContainer.value);
});

onBeforeUnmount(() => {
  // Cleanup
  safeDestroySwiper(swiperInstance);
  swiperInstance = null;

  if (observer) {
    observer.disconnect();
    observer = null;
  }
});

</script>

<style lang="scss">
@import 'swiper/css';
@import 'swiper/css/pagination';

.reviews-mobile-swiper-container {
	position: relative;
	min-height: 350px; // Предотвращение CLS
	margin: 0 -20px;
}

.reviews-mobile-swiper {
	padding: 0 20px;
	box-sizing: border-box;

	.swiper-pagination {
		position: relative;
		margin-top: 34px;

		& > * {
			opacity: 1;
			width: 10px !important;
			height: 10px !important;
			background-color: #EBEBEB;
			transition: background-color 0.3s ease;

			&.swiper-pagination-bullet-active {
				background-color: black;
			}
		}
	}
}

// Skeleton loader для Swiper
.reviews-mobile-swiper-skeleton {
	display: flex;
	gap: 16px;
	padding: 0 20px;
	overflow: hidden;
}

.skeleton-slide {
	min-width: calc(100% - 40px);
	height: 350px;
	border-radius: 20px;
	background: linear-gradient(
		90deg,
		#f4f3f1 25%,
		#ebebeb 50%,
		#f4f3f1 75%
	);
	background-size: 200% 100%;
	animation: shimmer 1.5s infinite;
	
	@media (min-width: 640px) {
		min-width: calc(50% - 28px);
	}
}

@keyframes shimmer {
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
}

// Performance optimization: использование will-change для анимаций
.swiper-slide {
	will-change: transform;
}
</style>
